function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import Choice from './Choice';
import { Label } from '../Label';
import React from 'react';
import classNames from 'classnames';
import describeField from '../utilities/describeField';
import useId from '../utilities/useId';
import { useLabelProps } from '../Label/useLabelProps';
import { useHint } from '../Hint/useHint';
import { useInlineError } from '../InlineError/useInlineError';

// Omit props that we override with values from the ChoiceList

/**
 * For information about how and when to use this component, refer to the
 * [checkbox](https://design.cms.gov/components/checkbox/) and
 * [radio](https://design.cms.gov/components/radio/) documentation pages.
 *
 * Checkboxes and radios can be managed as a group using `<ChoiceList>` or
 * individually using `<Choice>`. Note that each of the items in the `choices`
 * array represents props that will be passed to an individual `<Choice>`
 * component. You can therefore define any of the props listed in the `<Choice>`
 * props table below, including all valid attributes of the
 * [HTML input element](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input).
 */
export var ChoiceList = function ChoiceList(props) {
  var onBlur = props.onBlur,
    onComponentBlur = props.onComponentBlur,
    choices = props.choices;
  var id = useId('choice-list--', props.id);
  if (process.env.NODE_ENV !== 'production') {
    if (props.type !== 'checkbox' && props.choices.length === 1) {
      console.warn("[Warning]: Use type=\"checkbox\" for components with only one choice. A single radio button is disallowed because it prevents users from deselecting the field.");
    }
  }
  var choiceRefs = [];
  var handleBlur = function handleBlur(evt) {
    if (onBlur) onBlur(evt);
    if (onComponentBlur) handleComponentBlur(evt);
  };
  var handleComponentBlur = function handleComponentBlur(evt) {
    // The active element is always the document body during a focus
    // transition, so in order to check if the newly focused element
    // is one of our choices, we're going to have to wait a bit.
    setTimeout(function () {
      if (!choiceRefs.includes(document.activeElement)) {
        onComponentBlur(evt);
      }
    }, 20);
  };
  var _useInlineError = useInlineError(_objectSpread(_objectSpread({}, props), {}, {
      id: id
    })),
    errorId = _useInlineError.errorId,
    topError = _useInlineError.topError,
    bottomError = _useInlineError.bottomError,
    invalid = _useInlineError.invalid;
  var _useHint = useHint(_objectSpread(_objectSpread({}, props), {}, {
      id: id
    })),
    hintId = _useHint.hintId,
    hintElement = _useHint.hintElement;
  var labelProps = useLabelProps(_objectSpread(_objectSpread({}, props), {}, {
    id: id
  }));
  var choiceItems = choices.map(function (choiceProps, index) {
    var completeChoiceProps = _objectSpread(_objectSpread({
      // Allow this to be overridden by the choiceProps
      id: "".concat(id, "__choice--").concat(index)
    }, choiceProps), {}, {
      inversed: props.inversed,
      name: props.name,
      // onBlur: (onBlur || onComponentBlur) && handleBlur,
      onBlur: handleBlur,
      onChange: props.onChange,
      size: props.size,
      type: props.type,
      inputClassName: classNames(choiceProps.inputClassName, {
        'ds-c-choice--error': props.errorMessage
      }),
      disabled: choiceProps.disabled || props.disabled,
      // Individual choices can be disabled as well as the entire field
      inputRef: function inputRef(ref) {
        choiceRefs.push(ref);
        if (choiceProps.inputRef) {
          choiceProps.inputRef(ref);
        }
      }
    });
    return /*#__PURE__*/React.createElement(Choice, _extends({
      key: choiceProps.value
    }, completeChoiceProps));
  });
  return /*#__PURE__*/React.createElement("fieldset", {
    "aria-invalid": invalid,
    "aria-describedby": describeField(_objectSpread(_objectSpread({}, props), {}, {
      hintId: hintId,
      errorId: errorId
    })),
    className: classNames('ds-c-fieldset', props.className)
  }, /*#__PURE__*/React.createElement(Label, _extends({
    component: "legend"
  }, labelProps)), hintElement, topError, choiceItems, bottomError);
};
export default ChoiceList;